import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Estrategia de título de pestaña localizada.
 *
 * El `title` de cada ruta es una CLAVE i18n (`titles.*`). Esta estrategia la traduce con
 * Transloco al fijar `document.title`, y la re-traduce al cambiar el idioma en caliente.
 *
 * Solo afecta el título del navegador: NO interviene el matching ni la navegación de rutas,
 * por lo que no puede provocar errores de ruteo. Si una ruta trae un título que no es clave
 * (no migrado), se usa tal cual como respaldo.
 */
@Injectable({ providedIn: 'root' })
export class LocalizedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private currentKey: string | null = null;

  constructor() {
    super();
    this.transloco.langChanges$.subscribe(() => this.applyTitle());
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.currentKey = this.buildTitle(snapshot) ?? null;
    this.applyTitle();
  }

  private applyTitle(): void {
    if (!this.currentKey) {
      return;
    }
    const translated = this.transloco.translate(this.currentKey);
    // Clave inexistente → Transloco devuelve la propia clave; en ese caso usamos el texto literal.
    this.title.setTitle(translated && translated !== this.currentKey ? translated : this.currentKey);
  }
}
